import * as React from 'react';

import type { CommonlyExcludedHTMLAttributeNames, HTMLAttributes } from 'types/utils';
import { Breakpoint } from 'types/utils';

import { cx } from 'linaria';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { Styles } from './content-blocks.styles';
import { useTranslation } from 'react-i18next';

export namespace ContentBlocks {
  export type BannerImageProps = {
    breakpoint: Breakpoint;
    src: string;
    alt: string;
  } & Omit<HTMLAttributes, CommonlyExcludedHTMLAttributeNames>;

  export const BannerImage: React.FC<BannerImageProps> = ({
    breakpoint,
    src,
    alt,
    className,
    ...htmlProps
  }) => {
    const { t } = useTranslation();
    const { mobile, desktop, largeDesktop } =
      useOptimisedImages({
        src: src.split('?')[0], // fool-proof removing of image params
        sizes: {
          mobile: {
            w: '768',
            h: '134',
          },
          desktop: {
            w: '940',
            h: '176',
          },
          largeDesktop: {
            w: '1180',
            h: '200',
          },
        },
        options: {
          c: 'pad',
          b: 'white',
        },
      }) || {};

    return (
      <picture>
        <source media="(max-width: 768px)" srcSet={`${mobile?.x} 1x, ${mobile?.xx} 2x`} />
        <source media="(max-width: 1200px)" srcSet={`${desktop?.x} 1x, ${desktop?.xx} 2x`} />
        <source media="(min-width: 1200px)" srcSet={`${largeDesktop?.x} 1x, ${largeDesktop?.xx} 2x`} />
        <ImagePlaceholder
          src={desktop.x || ''}
          loading="lazy"
          alt={alt}
          className={cx(
            Styles.collectionMainPhoto.common,
            Styles.collectionMainPhoto[breakpoint],
            className,
          )}
          fallbackWidth={200}
          fallbackHeight={200}
          fallbackAlt={t('ui.image.alt')}
          {...htmlProps}
        />
      </picture>
    );
  };
}
